import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from '../../i18n/hooks';
import { Button, Input } from '../../ui';
import AuthService from '../services/AuthService';
import ValidationUtils from '../../../modules/utils/ValidationUtils';
import './ResetPasswordForm.css';

/**
 * ResetPasswordForm component - Form for resetting password with token
 * @param {Object} props - Component props
 * @param {string} props.token - Reset token from URL
 * @param {Function} props.onSuccess - Success callback
 * @param {Function} props.onBackToLogin - Back to login callback
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} ResetPasswordForm component
 */
const ResetPasswordForm = function ResetPasswordForm({
  token,
  onSuccess,
  onBackToLogin,
  className = ''
}) {
  const { t, language } = useTranslation();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [touched, setTouched] = useState({ password: false, confirmPassword: false });

  // Check if token is valid on mount
  useEffect(() => {
    if (!token) {
      setSubmitError(t('auth.resetPassword.invalidToken'));
    }
  }, [token, t]);

  // Password validation - Uses ValidationUtils from modules/utils (Component 12)
  const passwordValidationResult = ValidationUtils.validatePassword(password, {
    minLength: 8,
    requireUppercase: true,
    requireLowercase: true,
    requireNumbers: true,
    requireSpecialChars: true
  }, language || 'bg');
  
  // Convert ValidationResult to local format for backward compatibility
  const passwordValidation = {
    minLength: password.length >= 8,
    hasUpperCase: /[A-Z]/.test(password),
    hasLowerCase: /[a-z]/.test(password),
    hasNumbers: /\d/.test(password),
    hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    isValid: passwordValidationResult.isValid
  };
  
  // Use ValidationUtils for password confirmation
  const passwordConfirmResult = ValidationUtils.validatePasswordConfirm(password, confirmPassword, language || 'bg');
  const passwordsMatch = passwordConfirmResult.isValid;

  const errors = {
    password: touched.password && !password ? t('auth.validation.passwordRequired') :
              touched.password && !passwordValidation.isValid ? t('auth.validation.passwordInvalid') : null,
    confirmPassword: touched.confirmPassword && !confirmPassword ? t('auth.validation.confirmPasswordRequired') :
                     touched.confirmPassword && !passwordsMatch ? t('auth.validation.passwordsDoNotMatch') : null
  };

  const handlePasswordChange = useCallback((e) => {
    setPassword(e.target.value);
    setTouched(prev => ({ ...prev, password: true }));
    setSubmitError(null);
  }, []);

  const handleConfirmPasswordChange = useCallback((e) => {
    setConfirmPassword(e.target.value);
    setTouched(prev => ({ ...prev, confirmPassword: true }));
    setSubmitError(null);
  }, []);


  const handleSubmit = useCallback(async (ev) => {
    ev.preventDefault();
    setTouched({ password: true, confirmPassword: true });
    setSubmitError(null);
    setSubmitSuccess(false);

    if (Object.keys(errors).some(key => errors[key])) {
      return;
    }

    if (!passwordValidation.isValid) {
      setSubmitError(t('auth.validation.passwordInvalid'));
      return;
    }

    if (!passwordsMatch) {
      setSubmitError(t('auth.validation.passwordsDoNotMatch'));
      return;
    }

    console.log('🔍 ResetPasswordForm: Submitting with token:', token, 'password length:', password.length);
    setIsSubmitting(true);
    try {
      const { data, error } = await AuthService.resetPassword(token, password);
      console.log('📡 ResetPasswordForm: API response:', { data, error });
      
      if (error) {
        setSubmitError(error.message || t('errors.generic'));
        return;
      }
      if (data) {
        setSubmitSuccess(true);
        if (onSuccess) onSuccess(data);
      } else {
        setSubmitError(t('errors.generic'));
      }
    } catch (e) {
      console.log('💥 ResetPasswordForm: Exception caught:', e);
      setSubmitError(t('errors.network'));
    } finally {
      setIsSubmitting(false);
    }
  }, [password, confirmPassword, passwordValidation.isValid, passwordsMatch, errors, token, onSuccess, t]);

  const handleBackToLogin = useCallback(() => {
    if (onBackToLogin) {
      onBackToLogin();
    }
  }, [onBackToLogin]);

  if (!token) {
    return (
      <div className={`arbiwin-form arbiwin-form--reset-password ${className}`}>
        <div className="arbiwin-form__error" style={{
          background: '#f8d7da',
          border: '1px solid #f5c6cb',
          color: '#721c24',
          padding: '12px 16px',
          borderRadius: '4px',
          textAlign: 'center'
        }}>
          {t('auth.resetPassword.invalidToken')}
        </div>
        <div className="arbiwin-form__footer" style={{ marginTop: '16px' }}>
          <button
            onClick={handleBackToLogin}
            className="arbiwin-form__link"
          >
            {t('auth.resetPassword.backToLogin')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`arbiwin-form arbiwin-form--reset-password ${className}`}>
      <div className="arbiwin-form__header">
        <h2 className="arbiwin-form__title">
          {t('auth.resetPassword.title')}
        </h2>
        <p className="arbiwin-form__subtitle">
          {t('auth.resetPassword.subtitle')}
        </p>
      </div>

      {submitSuccess ? (
        <div className="arbiwin-form__group">
          <div className="arbiwin-form__success" style={{
            background: '#d4edda',
            border: '1px solid #c3e6cb',
            color: '#155724',
            padding: '12px 16px',
            borderRadius: '4px',
            marginBottom: '16px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
              <span style={{ fontSize: '20px', marginRight: '8px' }}>✅</span>
              <strong>{t('auth.resetPassword.success')}</strong>
            </div>
            <p style={{ margin: '0 0 12px 0', fontSize: '14px' }}>
              {t('auth.resetPassword.successMessage')}
            </p>
            <button 
              onClick={handleBackToLogin}
              style={{
                display: 'inline-block',
                background: '#28a745',
                color: 'white',
                padding: '8px 16px',
                borderRadius: '4px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
              onMouseOver={(e) => e.target.style.background = '#218838'}
              onMouseOut={(e) => e.target.style.background = '#28a745'}
            >
              {t('auth.resetPassword.backToLogin')}
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="arbiwin-form__form">
          {submitError && (
            <div className="arbiwin-form__error" style={{
              background: '#f8d7da',
              border: '1px solid #f5c6cb',
              color: '#721c24',
              padding: '12px 16px',
              borderRadius: '4px',
              marginBottom: '16px'
            }}>
              {submitError}
            </div>
          )}

          <div className="arbiwin-form__group">
            <label htmlFor="password" className="arbiwin-form__label">
              {t('auth.fields.newPassword')}
            </label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={handlePasswordChange}
              placeholder={t('auth.placeholders.newPassword')}
              error={errors.password}
              disabled={isSubmitting}
              className="arbiwin-form__input"
              showPassword
              required
            />
            {errors.password && (
              <span className="arbiwin-form__field-error">
                {errors.password}
              </span>
            )}
            
            {/* Password strength indicator */}
            {touched.password && password && (
              <div className="arbiwin-form__password-strength">
                <div className="arbiwin-form__password-requirements">
                  <div className={`arbiwin-form__requirement ${passwordValidation.minLength ? 'valid' : 'invalid'}`}>
                    {passwordValidation.minLength ? '✓' : '✗'} {t('auth.validation.minLength')}
                  </div>
                  <div className={`arbiwin-form__requirement ${passwordValidation.hasUpperCase ? 'valid' : 'invalid'}`}>
                    {passwordValidation.hasUpperCase ? '✓' : '✗'} {t('auth.validation.hasUpperCase')}
                  </div>
                  <div className={`arbiwin-form__requirement ${passwordValidation.hasLowerCase ? 'valid' : 'invalid'}`}>
                    {passwordValidation.hasLowerCase ? '✓' : '✗'} {t('auth.validation.hasLowerCase')}
                  </div>
                  <div className={`arbiwin-form__requirement ${passwordValidation.hasNumbers ? 'valid' : 'invalid'}`}>
                    {passwordValidation.hasNumbers ? '✓' : '✗'} {t('auth.validation.hasNumbers')}
                  </div>
                  <div className={`arbiwin-form__requirement ${passwordValidation.hasSpecialChar ? 'valid' : 'invalid'}`}>
                    {passwordValidation.hasSpecialChar ? '✓' : '✗'} {t('auth.validation.hasSpecialChar')}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="arbiwin-form__group">
            <label htmlFor="confirmPassword" className="arbiwin-form__label">
              {t('auth.fields.confirmNewPassword')}
            </label>
            <Input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={handleConfirmPasswordChange}
              placeholder={t('auth.placeholders.confirmNewPassword')}
              error={errors.confirmPassword}
              disabled={isSubmitting}
              className="arbiwin-form__input"
              showPassword
              required
            />
            {errors.confirmPassword && (
              <span className="arbiwin-form__field-error">
                {errors.confirmPassword}
              </span>
            )}
          </div>

          <div className="arbiwin-form__footer">
            <Button
              type="submit"
              variant="primary"
              size="large"
              disabled={isSubmitting || Object.keys(errors).some(key => errors[key]) || !passwordValidation.isValid || !passwordsMatch}
              className="arbiwin-form__submit-btn"
              fullWidth
            >
              {isSubmitting ? t('auth.resetPassword.resetting') : t('auth.resetPassword.resetPassword')}
            </Button>

            <div className="arbiwin-form__links">
              <button
                type="button"
                onClick={handleBackToLogin}
                className="arbiwin-form__link"
                disabled={isSubmitting}
              >
                {t('auth.resetPassword.backToLogin')}
              </button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
};

export default ResetPasswordForm;
